import { print } from 'graphql';

import { LIMIT_FOR_FETCHING_ADMIN_ITEMS, SORT_TYPE } from '~/shared/constants';
import {
  filesForExerciseLessonQuery,
  findByIdLessonQuery,
  generateContentLessonMutation,
  getSurveyAnalyticsByCourseQuery,
  lessonRelativeStatusQuery,
} from '~/shared/graphql-queries';
import createOneCopyLessonMutation from '~/shared/graphql-queries/lessons/create-one-copy-lesson-mutation';
import createOneLessonMutation from '~/shared/graphql-queries/lessons/create-one-lesson-mutation';
import findByAliasesLessonQuery from '~/shared/graphql-queries/lessons/find-by-aliases-lesson-query';
import findCurrentUnfinishedLessonQuery from '~/shared/graphql-queries/lessons/find-current-unfinished-lesson-query';
import findManyLessonQuery from '~/shared/graphql-queries/lessons/find-many-lesson-query';
import findOneLessonQuery from '~/shared/graphql-queries/lessons/find-one-lesson-query';
import getIdsByFilterLessonQuery from '~/shared/graphql-queries/lessons/get-ids-by-filter-lesson-query';
import { isLastLessonInModuleQuery } from '~/shared/graphql-queries/lessons/is-last-lesson-in-module-query';
import lessonFragment, { lightLessonFragment } from '~/shared/graphql-queries/lessons/lesson-fragment';
import removeByIdLessonMutation from '~/shared/graphql-queries/lessons/remove-by-id-lesson-mutation';
import updateByIdLessonMutation from '~/shared/graphql-queries/lessons/update-by-id-lesson-mutation';
import updateOrderLessonMutation from '~/shared/graphql-queries/lessons/update-order-lesson-mutation';
import { itemsPagination } from '~/shared/helpers/apollo-helpers';
import type { PageInfoType } from '~/shared/types/general-type';
import type { BuilderType } from '~/types';

type PaginationResult = Readonly<{
  items: Record<string, unknown>[];
  pageInfo: PageInfoType;
}>;

type PaginationLessonQueryResponse = Readonly<{
  data?: {
    paginationLessonQuery?: PaginationResult;
    paginationByCourseAndCategoryAliasLessonQuery?: PaginationResult;
  };
}>;

type SurveyAnalyticsResponse = Readonly<{
  data?: {
    getSurveyAnalyticsByCourse?: Record<string, unknown>;
  };
}>;

type FindCurrentUnfinishedLessonArgs = Readonly<{
  courseId?: string;
  userId?: string;
}>;

type GetIdsByFilterLessonArgs = Readonly<{
  filter?: Record<string, unknown>;
  sort?: string;
}>;

type GetSurveyAnalyticsByCourseArgs = Readonly<{
  course?: string;
  courseCategory?: string;
  student?: string;
}>;

type FindOneLessonArgs = Readonly<{
  filter?: Record<string, unknown>;
}>;

type FindByIdLessonArgs = Readonly<{
  _id?: string;
}>;

type FindByAliasesLessonArgs = Readonly<{
  alias?: string;
  courseCategoryAlias?: string;
  lessonAlias?: string;
  lessonTemplate?: string;
}>;

type FindManyLessonArgs = Readonly<{
  filter?: Record<string, unknown>;
}>;

type FilesForExerciseLessonArgs = Readonly<Record<string, unknown>>;

type PaginationLessonArgs = Readonly<{
  filter?: Record<string, unknown>;
  nextPage?: number;
  sort?: string;
  perPage?: number;
}>;

type UpdateByIdLessonArgs = Readonly<{
  _id?: string;
  record?: Record<string, unknown>;
}>;

type UpdateOrderLessonArgs = Readonly<{
  lessons?: unknown[];
}>;

type CreateOneLessonArgs = Readonly<{
  course?: string;
  student?: string;
  courseCategory?: string;
  isZeroLesson?: boolean;
  lessonTemplate?: string;
}>;

type RemoveByIdLessonArgs = Readonly<{
  _id?: string;
}>;


type GenerateContentLessonArgs = Readonly<{
  topic?: string;
  lng?: string;
}>;

type CreateOneCopyLessonArgs = Readonly<{
  beforeLessonId?: string;
  videoCallStatus?: string;
  courseId?: string;
  lessonId?: string;
  isZeroLesson?: boolean;
  lessonTemplate?: string;
  courseCategory?: string;
  student?: string;

}>;

const getPaginationTransform =
  (type: 'paginationLessonQuery' | 'paginationByCourseAndCategoryAliasLessonQuery') =>
  (response: PaginationLessonQueryResponse): PaginationResult => {
    return response.data?.[type] ?? { items: [], pageInfo: {} as PageInfoType };
  };

const getSurveyAnalyticsTransform = (response: SurveyAnalyticsResponse): Record<string, unknown> => {
  return response.data?.getSurveyAnalyticsByCourse ?? {};
};

export const lessonsTagTypes = ['lessons', 'lesson-template-lessons'] as const;

export const getLessonsQuery = (builder: BuilderType) => {
  return {
    findCurrentUnfinishedLesson: builder.query<unknown, FindCurrentUnfinishedLessonArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(findCurrentUnfinishedLessonQuery),
          variables: {
            courseId: args?.courseId,
            userId: args?.userId,
          },
        }),
      }),
    }),

    getIdsByFilterLesson: builder.query<unknown, GetIdsByFilterLessonArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(getIdsByFilterLessonQuery),
          variables: {
            filter: args?.filter,
            sort: args?.sort ?? SORT_TYPE._ID_ASC,
          },
        }),
      }),
    }),

    getSurveyAnalyticsByCourse: builder.query<Record<string, unknown>, GetSurveyAnalyticsByCourseArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(getSurveyAnalyticsByCourseQuery),
          variables: {
            course: args?.course,
            courseCategory: args?.courseCategory,
            student: args?.student,
          },
        }),
      }),
      transformResponse: getSurveyAnalyticsTransform,
    }),

    findOneLesson: builder.query<unknown, FindOneLessonArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(findOneLessonQuery),
          variables: {
            filter: args?.filter,
          },
        }),
      }),
    }),

    findByIdLesson: builder.query<unknown, FindByIdLessonArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(findByIdLessonQuery),
          variables: {
            _id: args?._id,
          },
        }),
      }),
    }),

    findByAliasesLesson: builder.query<unknown, FindByAliasesLessonArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(findByAliasesLessonQuery),
          variables: {
            alias: args?.alias,
            courseCategoryAlias: args?.courseCategoryAlias,
            lessonAlias: args?.lessonAlias,
            lessonTemplate: args?.lessonTemplate,
          },
        }),
      }),
    }),

    findManyLesson: builder.query<unknown, FindManyLessonArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(findManyLessonQuery),
          variables: {
            filter: args?.filter,
          },
        }),
      }),
    }),

    filesForExerciseLesson: builder.query<unknown, FilesForExerciseLessonArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(filesForExerciseLessonQuery),
          variables: args,
        }),
      }),
    }),

    paginationByCourseAndCategoryAliasLesson: builder.query<PaginationResult, PaginationLessonArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(
            itemsPagination({
              entity: 'Lesson',
              fragment: lightLessonFragment,
              fragmentName: 'LightLessonFragment',
              queryName: 'paginationByCourseAndCategoryAlias',
              filterType: 'FilterPaginationByCourseAndCategoryAliasLessonInput',
            }),
          ),
          variables: {
            filter: args?.filter,
            nextPage: args?.nextPage ?? 1,
            sort: args?.sort ?? '_ID_ASC',
            perPage: args?.perPage
              ? args.perPage
              : args?.nextPage === 1
                ? LIMIT_FOR_FETCHING_ADMIN_ITEMS + 1
                : LIMIT_FOR_FETCHING_ADMIN_ITEMS,
          },
        }),
      }),
      providesTags: ['lessons'],
      transformResponse: getPaginationTransform('paginationByCourseAndCategoryAliasLessonQuery'),
    }),

    paginationLessonTemplateLesson: builder.query<PaginationResult, PaginationLessonArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(
            itemsPagination({
              entity: 'Lesson',
              fragment: lessonFragment,
            }),
          ),
          variables: {
            filter: args?.filter,
            nextPage: args?.nextPage ?? 1,
            sort: args?.sort ?? '_ID_ASC',
            perPage: args?.perPage
              ? args.perPage
              : args?.nextPage === 1
                ? LIMIT_FOR_FETCHING_ADMIN_ITEMS + 1
                : LIMIT_FOR_FETCHING_ADMIN_ITEMS,
          },
        }),
      }),
      providesTags: ['lesson-template-lessons'],
      transformResponse: getPaginationTransform('paginationLessonQuery'),
    }),
    isLastLessonInModule: builder.query<{ data: { isLastLessonInModule: boolean } }, { lessonId: string }>({
      query: ({ lessonId }) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(isLastLessonInModuleQuery),
          variables: { lessonId },
        }),
      }),
    }),
    lessonRelativeStatus: builder.query<{ data: { isLastLessonInModule: boolean } }, { lessonId: string }>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(lessonRelativeStatusQuery),
          variables: args,
        }),
      }),
    }),
    updateByIdLesson: builder.mutation<unknown, UpdateByIdLessonArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(updateByIdLessonMutation),
          variables: {
            _id: args?._id,
            record: args?.record,
          },
        }),
      }),
    }),

    updateOrderLesson: builder.mutation<unknown, UpdateOrderLessonArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(updateOrderLessonMutation),
          variables: {
            lessons: args?.lessons,
          },
        }),
      }),
    }),

    createOneLesson: builder.mutation<unknown, CreateOneLessonArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(createOneLessonMutation),
          variables: {
            course: args?.course,
            student: args?.student,
            courseCategory: args?.courseCategory,
            isZeroLesson: args?.isZeroLesson,
            lessonTemplate: args?.lessonTemplate,
          },
        }),
      }),
    }),

    removeByIdLesson: builder.mutation<unknown, RemoveByIdLessonArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(removeByIdLessonMutation),
          variables: {
            _id: args?._id,
          },
        }),
      }),
    }),

    generateContentLesson: builder.mutation<unknown, GenerateContentLessonArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(generateContentLessonMutation),
          variables: {
            topic: args?.topic,
            lng: args?.lng,
          },
        }),
      }),
    }),

    createOneCopyLesson: builder.mutation<unknown, CreateOneCopyLessonArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(createOneCopyLessonMutation),
          variables: {
            videoCallStatus: args?.videoCallStatus,
            courseId: args?.courseId,
            lessonId: args?.lessonId,
            beforeLessonId: args?.beforeLessonId,
            isZeroLesson: args?.isZeroLesson,
            lessonTemplate: args?.lessonTemplate,
            courseCategory: args?.courseCategory,
            student: args?.student,
          },
        }),
      }),

    }),
  };
};
